import type { PropertiesNullable } from '@@types/helpers'
import EntityBase from '@app/entities/EntityBase'
import { EntityHealthCheckAttribute, createEntities } from '@app/entities/index'
import type {
  HealthCheckAttribute,
  HealthCheckDomainStatus,
  HealthCheckDomainStatusReason,
  Maybe
} from '@server/graphql/typeDefs/types'
import { HealthCheckStatus } from '@server/graphql/typeDefs/types'

/** Translation key of the different available labels */
export enum HealthCheckLabelTranslation {
  name = 'name',
  description = 'description',
  details = 'details',
  unknownDetails = 'unknownDetails',
  successDetails = 'successDetails',
  remediation = 'remediation'
}

export default class EntityHealthCheckDomainStatusReason
  extends EntityBase
  implements PropertiesNullable<HealthCheckDomainStatusReason>
{
  name: Maybe<string> = null
  status: Maybe<HealthCheckStatus> = null
  statusSince: Maybe<string> = null
  lastCheckDate: Maybe<string> = null
  attributes: Maybe<HealthCheckAttribute[]> = null

  constructor(data: Partial<HealthCheckDomainStatus>) {
    super()
    Object.assign(this, data)
  }

  /**
   * Return the attributes entities.
   */
  getAttributes(): Maybe<EntityHealthCheckAttribute[]> {
    if (!this.attributes) {
      return null
    }

    return createEntities<HealthCheckAttribute, EntityHealthCheckAttribute>(
      EntityHealthCheckAttribute,
      this.attributes
    )
  }

  /**
   * Return true if the reason status is unknown.
   */
  isStatusUnknown(): boolean {
    if (!this.status) {
      return false
    }

    return this.status === HealthCheckStatus.Unknown
  }

  /**
   * Return true if the reason status is warning or failure.
   */
  isWarningOrFailure(): boolean {
    if (!this.status) {
      return false
    }

    return [HealthCheckStatus.Warning, HealthCheckStatus.Failure].includes(
      this.status
    )
  }

  /**
   * Return the label according to the status of the reason.
   */
  statusLabelTranslation(): Maybe<HealthCheckLabelTranslation> {
    if (this.isStatusUnknown()) {
      return HealthCheckLabelTranslation.unknownDetails
    }

    // fail status if warn | failure
    if (this.isWarningOrFailure()) {
      return HealthCheckLabelTranslation.details
    }

    // otherwise, success
    return HealthCheckLabelTranslation.successDetails
  }
}
